import rvalues from '../../../../data/rvalues.json'; // R-values for era 0 to 4, with 3 climate zones
import rvaluesv2 from '../../../../data/rvaluesv2.json'; // R-values for era 5 and 6, with 6 climate zones

/**
 * Returns the appropriate R-values based on the specified era.
 *
 * @param {string} era - The era for which the R-values are needed, e.g., 'From 3 Nov 2022 to 30 Apr 2023'.
 * @returns {Object} An object containing the R-values for the specified era.
 * @example
 * // For era 'From 3 Nov 2022 to 30 Apr 2023', returns rvaluesv2
 * getRValuesByEra('From 3 Nov 2022 to 30 Apr 2023');
 */
const getRValuesByEra = (era) => {
    if (era === 'From 1 May 2023 to 1 Nov 2023' || era === 'On or after 2 Nov 2023') {
        return rvaluesv2;
    }

    return rvalues;
};

const prepObject = object => {
        const obj = object;
        // Determine the appropriate R-values JSON based on era
        obj.rvalues = getRValuesByEra(obj.era);

        switch (obj.era) {
            case 'Before 1978':
                obj.era = 0;
                break;
            case 'From 1978 to 2000':
                obj.era = 1;
                break;
            case 'From 2001 to 2008':
                obj.era = 2;
                break;
            case 'From 2009 to 2 Nov 2022':
                obj.era = 3;
                break;
            case 'From 3 Nov 2022 to 30 Apr 2023':
                obj.era = 4;
                break;
            case 'From 1 May 2023 to 1 Nov 2023':
                obj.era = 5;
                break;
            case 'On or after 2 Nov 2023':
                obj.era = 6;
                break;
            default:
                obj.era = 0;
                break;
        }

        // Create a zoneMapping object to map input zones to the correct climate zones. Zones will be different for eras 5 and 6.
        const zoneMapping = {
            1: 'zone1',
            2: 'zone2',
            3: 'zone3'
        };

        // If the era is 5 or 6, extend the mapping object to include zones 4, 5, and 6
        if (obj.era === 5 || obj.era === 6) {
            zoneMapping[4] = 'zone4';
            zoneMapping[5] = 'zone5';
            zoneMapping[6] = 'zone6';
        }

        // Set obj.zone based on the mapping object, default to 'zone1' if input zone is not in the mapping object
        obj.zone = zoneMapping[obj.zone] || 'zone1';

        return obj;
    },

    ceilingRValue = object => {
        const obj = prepObject(object);

        // use defaultSurface if obj.surface is not defined
        if (
            typeof obj.surface === typeof undefined ||
        typeof obj.rvalues.ceiling[obj.surface] === typeof undefined
        ) {
            obj.surface = obj.rvalues.ceiling.defaultSurface;
        }

        // external is a little more complicated and needs to
        // lookup the compliant (standard) vs default values
        if (obj.surface === 'external') {
            if (
                typeof obj.standard === typeof undefined || (obj.standard !== 'standard' && obj.standard !== 'nonstandard' && obj.standard !== 'none')
            ) {
                obj.standard = 'nonstandard';
            }

            return obj.rvalues.ceiling[obj.surface][obj.zone][obj.era][obj.standard];
        }

        return obj.rvalues.ceiling[obj.surface][obj.zone][obj.era];
    },

    floorRValue = object => {
        const obj = prepObject(object);

        // use defaultSurface if obj.surface is not defined
        if (
            typeof obj.surface === typeof undefined ||
        typeof obj.rvalues.floor[obj.surface] === typeof undefined
        ) {
            obj.surface = obj.rvalues.ceiling.defaultSurface;
        }

        // external is a little more complicated and needs to
        // lookup the compliant (standard) vs default values
        if (obj.surface === 'external' || obj.surface === 'slab') {
            if (
                typeof obj.standard === typeof undefined || (obj.standard !== 'standard' && obj.standard !== 'nonstandard' && obj.standard !== 'none')
            ) {
                obj.standard = 'nonstandard';
            }

            return obj.rvalues.floor[obj.surface][obj.zone][obj.era][obj.standard];
        }

        return obj.rvalues.floor[obj.surface][obj.zone][obj.era];
    },

    wallRValue = object => {
        const obj = prepObject(object);

        // use defaultSurface if obj.surface is not defined
        if (
            typeof obj.surface === typeof undefined ||
        typeof obj.rvalues.floor[obj.surface] === typeof undefined
        ) {
            obj.surface = obj.rvalues.wall.defaultSurface;
        }

        return obj.rvalues.wall[obj.surface][obj.zone][obj.era];
    },

    glazingRValue = object => {
        const obj = prepObject(object);

        // use defaultGlazing if obj.glazing is not defined
        if (
            typeof obj.glazing === typeof undefined ||
        typeof obj.rvalues.glazing[obj.zone][obj.era][obj.glazing] === typeof undefined
        ) {
            obj.glazing = obj.rvalues.glazing.defaultGlazing;
        }

        return obj.rvalues.glazing[obj.zone][obj.era][obj.glazing];
    },

    skylightRValue = object => {
        const obj = prepObject(object);

        // use defaultGlazing if obj.glazing is not defined
        if (
            typeof obj.glazing === typeof undefined ||
        typeof obj.rvalues.skylight[obj.zone][obj.era][obj.glazing] === typeof undefined
        ) {
            obj.glazing = obj.rvalues.skylight.defaultGlazing;
        }

        return obj.rvalues.skylight[obj.zone][obj.era][obj.glazing];
    },

    rValueForElement = object => {
        let value;

        switch (object.element) {
            case 'ceiling':
                value = ceilingRValue(object);
                break;
            case 'floor':
                value = floorRValue(object);
                break;
            case 'wall':
                value = wallRValue(object);
                break;
            case 'glazing':
                value = glazingRValue(object);
                break;
            case 'skylight':
                value = skylightRValue(object);
                break;
            default:
                value = null;
                break;
        }

        return value;
    },

    rValuesAll = object => {
        const values = {
            ceiling: ceilingRValue(object),
            floor: floorRValue(object),
            wall: wallRValue(object),
            glazing: glazingRValue(object),
            skylight: skylightRValue(object)
        };

        return values;
    };

export default {
    ceilingRValue,
    floorRValue,
    wallRValue,
    glazingRValue,
    skylightRValue,
    rValueForElement,
    rValuesAll
};
